import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import { authFetch } from '../api/api';
import AuthPageLayout, {
  pagePaperSx,
  primaryButtonSx,
  stepperSx,
  textFieldSx,
} from '../components/AuthPageLayout';
import LoggedInGate from '../components/LoggedInGate';
import MessageModal from '../components/MessageModal';
import {
  extractAuthPayload,
  getApiErrorMessage,
  normalizePhone,
  readJsonSafe,
  completeAuthSession,
} from '../utils/authSession';

const STEPS = ['login_step_phone', 'login_step_otp'];

export default function SignIn() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [phone, setPhone] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageModal, setMessageModal] = useState({ visible: false, title: '', message: '', variant: 'info' });

  const showMessage = useCallback((title, message, variant = 'error') => {
    setMessageModal({ visible: true, title, message, variant });
  }, []);

  const finishAuth = useCallback(async (json) => {
    const { token, user } = extractAuthPayload(json);
    if (!token) {
      showMessage(t('login_error'), t('login_token_missing'));
      return;
    }
    await completeAuthSession({ token, user: user ?? { phone: normalizedPhone } });
    window.dispatchEvent(new Event('profile-updated'));
    navigate('/profile', { replace: true });
  }, [navigate, normalizedPhone, showMessage, t]);

  const handleSendOtp = async () => {
    const formattedPhone = normalizePhone(phone);
    if (formattedPhone.length < 10) {
      showMessage(t('login_error'), t('login_phone_invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await authFetch('/users/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: formattedPhone }),
      });
      if (!response.ok) {
        showMessage(t('login_error'), await getApiErrorMessage(response, t('login_request_failed')));
        return;
      }
      setNormalizedPhone(formattedPhone);
      setStep(1);
      showMessage(t('login_otp_sent'), t('login_otp_sent_message'), 'success');
    } catch {
      showMessage(t('login_error'), t('login_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (otp.trim().length < 4) {
      showMessage(t('login_error'), t('login_otp_invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await authFetch('/users/auth/login', {
        method: 'POST',
        body: JSON.stringify({ phone: normalizedPhone, otp: otp.trim() }),
      });
      const json = await readJsonSafe(response);
      if (!response.ok) {
        const message = json.message ?? t('login_request_failed');
        if (response.status === 404 || message.toLowerCase().includes('sign up')) {
          showMessage(t('login_error'), t('signin_no_account'));
          return;
        }
        showMessage(t('login_error'), message);
        return;
      }
      await finishAuth(json);
    } catch {
      showMessage(t('login_error'), t('login_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout title={t('Login')} subtitle={t('signin_subtitle')}>
      <LoggedInGate>
        <Paper {...pagePaperSx}>
          <Box sx={{ bgcolor: alpha('#B8894A', 0.04), borderBottom: 1, borderColor: 'divider' }}>
            <Stepper activeStep={step} alternativeLabel sx={stepperSx}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{t(label)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {step === 0 ? (
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label={t('login_phone')}
                  placeholder={t('login_phone_placeholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  type="tel"
                  sx={textFieldSx}
                  InputProps={{
                    startAdornment: <PhoneOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />,
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSendOtp}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? '...' : t('login_send_otp')}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderRadius: 3,
                    textAlign: 'center',
                    bgcolor: alpha('#B8894A', 0.07),
                    border: 1,
                    borderColor: alpha('#B8894A', 0.14),
                  }}
                >
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {t('login_phone')}
                  </Typography>
                  <Typography fontWeight={800} color="primary.main" sx={{ mt: 0.25 }}>
                    {normalizedPhone}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label={t('login_otp')}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  sx={textFieldSx}
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: 6, fontSize: 20 } }}
                  InputProps={{
                    startAdornment: <PinOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />,
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleLogin}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? '...' : t('Login')}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setStep(0)}
                  sx={{ fontWeight: 700, color: 'primary.main' }}
                >
                  {t('login_phone')}
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('register_subtitle')}{' '}
            <Link
              component="button"
              variant="body2"
              fontWeight={800}
              color="primary"
              onClick={() => navigate('/register')}
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('Register')}
            </Link>
          </Typography>
        </Box>
      </LoggedInGate>

      <MessageModal {...messageModal} onClose={() => setMessageModal((p) => ({ ...p, visible: false }))} />
    </AuthPageLayout>
  );
}
